import React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { logEvent } from 'firebase/analytics';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { firestore, invariant, loadAnalytics } from '../../../lib';
import { Button } from '../../commons';
import { Input, Select } from '../../inputs';
import { Application, Category } from '../../../models';

interface CategoryFormProps {
  application: Application;
  category: string;
  categories: Category[];
  isReadOnly?: boolean;
}

export default function CategoryForm({
  application,
  category: _category = 'none',
  categories = [],
  isReadOnly = false,
}: CategoryFormProps) {
  const router = useRouter();
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      invariant(
        formData.get('category')?.toString()?.trim(),
        'Category is required'
      );
      invariant(
        formData.get('category')?.toString()?.trim() !== 'none',
        'Category is required'
      );
      NProgress.start();
      await updateDoc(doc(firestore, 'applications', application?.id), {
        step: '2',
        category: formData.get('category')?.toString()?.trim() ?? '',
        lastUpdated: Timestamp.fromDate(new Date()),
      });
      logEvent(loadAnalytics(), 'step_completed', {
        step: '2',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '2',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        `/request-scholarship/3`
      );
    } catch (err) {
      toast.error(
        'Error updating your application, check your internet. CODE: E21',
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      posthog?.captureException(err);
      Sentry.addBreadcrumb({ level: 'error', category: 'step-2' });
      Sentry.captureException(err);
    } finally {
      NProgress.done();
    }
  };
  return (
    <form method="post" onSubmit={onSubmit}>
      {isReadOnly ? (
        <Input
          type="text"
          name="category"
          label="Category"
          defaultValue={_category}
          disabled
          readOnly
        />
      ) : (
        <Select
          label="Scholarship Category"
          name="category"
          defaultValue={_category || 'none'}
          disabled={isReadOnly}
          required
        >
          <option value="none" disabled>
            Select a scholarship category
          </option>
          {categories.map((category) => (
            <option value={category.name} key={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
      )}
      {isReadOnly ? null : (
        <Button type="submit" color="blue" className="ml-auto w-[135px]">
          Continue
        </Button>
      )}
    </form>
  );
}
